import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
export function AuthorSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="w-full bg-[var(--cream)] py-24 md:py-32" data-id="element-16">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.8
    }} className="max-w-6xl mx-auto px-6" data-id="element-17">
        <div className="grid md:grid-cols-5 gap-12 items-center" data-id="element-18">
          {/* Left: Author monogram */}
          <div className="md:col-span-2" data-id="element-19">
            <div className="relative w-full aspect-[3/4] max-w-sm mx-auto rounded-2xl overflow-hidden shadow-2xl border border-[var(--warm-gold)]/40" data-id="element-20">
              {/* Sky / dawn gradient */}
              <svg viewBox="0 0 300 400" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" aria-hidden="true" data-id="element-21">
                <defs data-id="element-22">
                  <linearGradient id="dawnSky" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-23">
                    <stop offset="0%" stopColor="#3d5a4f" data-id="element-24" />
                    <stop offset="55%" stopColor="#d4856b" data-id="element-25" />
                    <stop offset="85%" stopColor="#d4a574" data-id="element-26" />
                    <stop offset="100%" stopColor="#faf3e7" data-id="element-27" />
                  </linearGradient>
                  <radialGradient id="sun" cx="50%" cy="62%" r="18%" data-id="element-28">
                    <stop offset="0%" stopColor="#faf3e7" stopOpacity="1" data-id="element-29" />
                    <stop offset="60%" stopColor="#d4a574" stopOpacity="0.6" data-id="element-30" />
                    <stop offset="100%" stopColor="#d4a574" stopOpacity="0" data-id="element-31" />
                  </radialGradient>
                  <linearGradient id="hill" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-32">
                    <stop offset="0%" stopColor="#2a2018" data-id="element-33" />
                    <stop offset="100%" stopColor="#3a2e26" data-id="element-34" />
                  </linearGradient>
                </defs>

                {/* Sky */}
                <rect width="300" height="400" fill="url(#dawnSky)" data-id="element-35" />

                {/* Sun glow */}
                <circle cx="150" cy="248" r="80" fill="url(#sun)" data-id="element-36" />
                <circle cx="150" cy="248" r="22" fill="#faf3e7" opacity="0.95" data-id="element-37" />

                {/* Stars (top, subtle) */}
                <circle cx="40" cy="50" r="1" fill="#faf3e7" opacity="0.7" data-id="element-38" />
                <circle cx="80" cy="30" r="1.2" fill="#faf3e7" opacity="0.5" data-id="element-39" />
                <circle cx="220" cy="60" r="1" fill="#faf3e7" opacity="0.6" data-id="element-40" />
                <circle cx="260" cy="35" r="1.3" fill="#faf3e7" opacity="0.5" data-id="element-41" />
                <circle cx="180" cy="20" r="0.9" fill="#faf3e7" opacity="0.4" data-id="element-42" />

                {/* Back hill */}
                <path d="M 0 310 Q 90 270 160 290 T 300 280 L 300 400 L 0 400 Z" fill="#2a2018" opacity="0.5" data-id="element-43" />

                {/* Front hill with silhouette */}
                <path d="M 0 340 Q 100 305 160 320 T 300 315 L 300 400 L 0 400 Z" fill="url(#hill)" data-id="element-44" />

                {/* Walking figure silhouette */}
                <g transform="translate(150, 280)" fill="#1a1410" data-id="element-45">
                  {/* head */}
                  <circle cx="0" cy="0" r="6" data-id="element-46" />
                  {/* body */}
                  <path d="M -5 5 Q -6 18 -4 30 L 4 30 Q 6 18 5 5 Z" data-id="element-47" />
                  {/* legs */}
                  <path d="M -4 30 L -6 48 L -3 48 L -1 32 Z" data-id="element-48" />
                  <path d="M 4 30 L 7 48 L 4 48 L 1 32 Z" data-id="element-49" />
                  {/* arms */}
                  <path d="M -5 10 L -10 24 L -8 25 L -3 12 Z" data-id="element-50" />
                  <path d="M 5 10 L 9 22 L 7 23 L 3 12 Z" data-id="element-51" />
                </g>

                {/* Soft grain/noise overlay via subtle dots */}
                <g opacity="0.08" fill="#faf3e7" data-id="element-52">
                  <circle cx="20" cy="100" r="0.5" data-id="element-53" />
                  <circle cx="270" cy="120" r="0.5" data-id="element-54" />
                  <circle cx="140" cy="80" r="0.5" data-id="element-55" />
                  <circle cx="60" cy="180" r="0.5" data-id="element-56" />
                  <circle cx="240" cy="200" r="0.5" data-id="element-57" />
                </g>
              </svg>

              {/* Signature plate */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[var(--midnight)] via-[var(--midnight)]/90 to-transparent p-6 pt-12" data-id="element-58">
                <p className="text-xs uppercase tracking-[0.25em] text-[var(--warm-gold)]/80 mb-1" data-id="element-59">
                  El Autor
                </p>
                <p className="font-display italic text-2xl text-[var(--cream)] leading-tight" data-id="element-60">
                  Miguel A. Pérez
                </p>
              </div>
            </div>
          </div>

          {/* Right: Story */}
          <div className="md:col-span-3 space-y-6" data-id="element-61">
            <h2 className="font-display text-4xl md:text-5xl text-[var(--dusk-purple)]" data-id="element-62">
              Conoce El Vuelo del Renacer
            </h2>

            <div className="space-y-4 text-lg text-[var(--charcoal)] leading-relaxed" data-id="element-63">
              <p className="italic text-xl text-[var(--dusk-purple)]" data-id="element-64">
                El Vuelo del Renacer nació de un lugar oscuro.
              </p>

              <p data-id="element-65">
                Su autor, <strong data-id="element-66">Miguel A. Pérez</strong>, sabe lo que es
                manejar por la autopista a toda velocidad, con el alma hecha
                pedazos y una voz fría susurrándole desde adentro:{' '}
                <em data-id="element-67">"Solo gira el volante. Acaba con todo de una vez."</em>
              </p>

              <p data-id="element-68">
                Sabe lo que es estar en una reunión, rodeado de gente, y sentir
                un vacío tan profundo que el ruido solo lo hace más grande.
              </p>

              <p data-id="element-69">
                Sabe lo que es sonreír al mundo mientras por dentro algo se
                muere.
              </p>

              <p className="text-xl font-semibold text-[var(--sunset-coral)] pt-4" data-id="element-70">
                Y desde ese lugar — no desde un escenario perfecto, no desde
                teorías académicas, sino desde el barro — construyó un proceso
                real. Un mapa. Una guía que no te pide que estés bien para
                empezar. Solo que des el primer paso.
              </p>

              <p className="font-display text-2xl italic text-[var(--dusk-purple)] pt-4" data-id="element-71">
                Este libro no es teoría. Es el camino.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}